import { createContext, useContext, useState, useEffect, useRef, type ReactNode } from 'react'
import { useTranslation } from 'react-i18next'
import { getDeviceInfoDS, getUserInfoDS } from '@/utils/webview-fun'
import { locales, defaultLocale, Locale } from '@/i18n/config'
import { session } from '@/utils/storage'

interface AppInitializerContextType {
  isInitialized: boolean
  isInitializing: boolean
}

const AppInitializerContext = createContext<AppInitializerContextType>({
  isInitialized: false,
  isInitializing: true,
})

export const useAppInitializer = () => useContext(AppInitializerContext)

interface AppInitializerProviderProps {
  children: ReactNode
}

/**
 * 应用初始化 Provider 组件
 * 职责：
 * 1. 从 JSBridge 获取设备语言和用户信息
 * 2. 设置 i18next 语言（i18next 会自动触发重新渲染）
 * 3. 保存用户信息到 sessionStorage（供 API 请求使用）
 */
export function AppInitializerProvider({ children }: AppInitializerProviderProps) {
  const { i18n } = useTranslation()
  const [isInitialized, setIsInitialized] = useState(false)
  const [isInitializing, setIsInitializing] = useState(true)
  const hasInitialized = useRef(false)

  useEffect(() => {
    // 如果已经初始化过，不再执行
    if (hasInitialized.current) return

    const initializeApp = () => {
      try {
        console.log('[AppInitializer] 开始初始化...')

        // 1. 获取用户信息（会自动保存到 sessionStorage）
        const userInfo = getUserInfoDS()
        console.log('[AppInitializer] 用户信息已获取:', userInfo)

        // 2. 获取设备信息（会自动保存到 sessionStorage）
        const deviceInfo = getDeviceInfoDS()
        const deviceLanguage = deviceInfo?.language || ''
        console.log('[AppInitializer] 设备语信息已获取:', deviceInfo)
        console.log('[AppInitializer] 设备语言已获取:', deviceLanguage)

        // 3. 确定目标语言（优先级：URL lang 参数 > deviceLanguage > defaultLocale）
        let targetLocale = defaultLocale

        // 3.1 优先从 URL 的 lang 参数获取
        const urlParams = new URLSearchParams(window.location.search)
        const urlLang = urlParams.get('lang')
        if (urlLang && locales.includes(urlLang as any)) {
          targetLocale = urlLang as Locale
          console.log(`[AppInitializer] 使用 URL lang 参数: ${targetLocale}`)
        }
        // 3.2 其次从 deviceLanguage 获取
        else if (deviceLanguage && locales.includes(deviceLanguage as any)) {
          targetLocale = deviceLanguage as Locale
          console.log(`[AppInitializer] 使用设备语言: ${targetLocale}`)
        }
        // 3.3 最后使用默认语言
        else {
          console.log(`[AppInitializer] 使用默认语言: ${targetLocale}`)
        }

        session.set('language', targetLocale)

        // 4. 设置 i18next 语言（i18next 会自动触发组件重新渲染）
        if (i18n.language !== targetLocale) {
          console.log(`[AppInitializer] 切换语言: ${i18n.language} -> ${targetLocale}`)
          i18n.changeLanguage(targetLocale)
        }

        // 仅在开发环境下强制切换到阿拉伯语（用于测试 RTL 布局）
        // if (import.meta.env.DEV) {
        // i18n.changeLanguage('ur')
        // }

        // 5. 标记初始化完成
        setIsInitialized(true)
        setIsInitializing(false)
        hasInitialized.current = true
        console.log('[AppInitializer] 初始化完成')
      } catch (error) {
        console.error('[AppInitializer] 初始化失败:', error)

        // 容错处理：即使出错也标记为已初始化，避免阻塞应用
        setIsInitialized(true)
        setIsInitializing(false)
        hasInitialized.current = true
      }
    }

    initializeApp()
  }, [i18n])

  return (
    <AppInitializerContext.Provider value={{ isInitialized, isInitializing }}>
      {children}
    </AppInitializerContext.Provider>
  )
}
